import React, { useContext } from 'react'

import { TextBlockContext } from '../text'
import {
  composeGroups,
  createView,
  flattenStyle,
  flexItem,
  layout,
  spacing,
  type KitViewProps
} from '../_support'
import { colors } from '@theme'

/* -----------------------------------------------------------------------------
 * SVG
 *
 * An svg component inside a View that takes spacing, flex and display props.
 * `width`, `height`, `fill`, `stroke` and `style` go to the svg itself, with
 * theme color keys (`fill='violet'`) resolved to their values.
 * -------------------------------------------------------------------------- */

const SVGContainer = createView({
  displayName: 'SVGContainer',
  groups: composeGroups({ display: layout.display }, flexItem, spacing)
})

export type SVGComponentType = React.ComponentType<
  React.SVGProps<SVGSVGElement>
>

export type SVGProps = KitViewProps & {
  svg: SVGComponentType
  width?: number | string
  height?: number | string
  fill?: string
  stroke?: string
}

const resolveColor = (value: string) =>
  value in colors ? colors[value as keyof typeof colors] : value

const SVG = ({
  svg: Svg,
  width,
  height,
  fill,
  stroke,
  style,
  ...rest
}: SVGProps) => {
  const containerProps = { ...useContext(TextBlockContext), ...rest }
  const svgProps = {
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
    ...(fill !== undefined ? { fill: resolveColor(fill) } : {}),
    ...(stroke !== undefined ? { stroke: resolveColor(stroke) } : {}),
    ...flattenStyle(style)
  }
  return (
    <SVGContainer {...containerProps}>
      <Svg {...svgProps} />
    </SVGContainer>
  )
}

// the props the svg takes, for components that split theirs between parts
SVG.styleKeys = new Set(['fill', 'stroke', 'width', 'height'])

export default SVG
